import { EPropertyTypeId } from "../types/property.types";
import { getLocalizedText } from "./i18n/i18n.utils";
// ── Configurable weights — change these to rebalance scoring ──────────
export var WEIGHTS = {
    income: 50,
    household: 40,
    date: 10,
};
/**
 * The office table as it is stored on the broker: text holding a JSON array.
 *
 * Anything unreadable reads as "nothing set", which is the same as an empty
 * column and scores every property by the sliding scale. A broker's table is
 * not worth an exception thrown into a score.
 */
export var parseIncomeRatioRanges = function (stored) {
    if (!stored) {
        return undefined;
    }
    try {
        // The table crosses an API that hands it back in either shape. The legacy
        // broker model declares the column as a list of objects stored in TEXT, so
        // it arrives already parsed; other callers hold the raw string. Parsing a
        // list would throw on "[object Object]", be swallowed by the catch below,
        // and silently score every property on the built-in scale instead of on
        // the table the office filled in.
        var parsed = Array.isArray(stored) ? stored : JSON.parse(stored);
        if (!Array.isArray(parsed) || parsed.length === 0) {
            return undefined;
        }
        // Nothing is converted, only checked. Number(null) and Number("") are both
        // a perfectly finite 0, and a ratio of 0 would hand everybody a perfect
        // income score, so a value that is not already a number is a broken table
        // rather than a zero.
        var isUsableNumber_1 = function (value) {
            return typeof value === "number" && isFinite(value) && value > 0;
        };
        var points = parsed.map(function (row) {
            return isUsableNumber_1(row === null || row === void 0 ? void 0 : row.maxRent) && isUsableNumber_1(row === null || row === void 0 ? void 0 : row.ratio)
                ? { maxRent: row.maxRent, ratio: row.ratio }
                : undefined;
        });
        // One unusable row discards the table rather than half-applying it. Half a
        // table would score one stretch of rents on something nobody chose, which
        // is worse than scoring everything the way it was scored before any of this
        // existed. A row with no rent at all lands here too: since DEV-3265 a row
        // is a point on a curve, and a point without a rent anchors nothing.
        if (!points.every(function (point) { return !!point; })) {
            return undefined;
        }
        var usable_1 = points;
        // Strictly increasing. Two points at the same rent would ask for a vertical
        // line, and points out of order would run the curve backwards between them:
        // both quietly plausible and both wrong. Refused whole, like every other
        // unreadable table, rather than sorted into something the office never
        // typed.
        var isAscending = usable_1.every(function (point, index) {
            return index === 0 || point.maxRent > usable_1[index - 1].maxRent;
        });
        return isAscending ? usable_1 : undefined;
    }
    catch (_a) {
        return undefined;
    }
};
export var formatIncomeRatioRanges = function (ranges) {
    return JSON.stringify(ranges);
};
/**
 * The lowest income a broker may name for a perfect score, as a multiple of the
 * rent. Below this the derived floor would drop under the rent itself, which
 * would award points to somebody who cannot pay the rent at all. Limited on the
 * way in rather than rescued afterwards: a silent correction would make the
 * entered number mean something other than what it says, invisibly, because the
 * floor is never shown.
 */
export var MIN_PERFECT_INCOME_FACTOR = 1.5;
// ── A. Income helpers ─────────────────────────────────────────────────
/**
 * What the built-in scale asks at one rent, as a multiple of it.
 *
 * Exported so a screen can show the standard by asking the scale itself rather
 * than by repeating numbers that would drift the moment the scale is touched.
 */
export var getStandardPerfectRatio = function (rent) {
    return getDefaultRatios(rent).perfectRatio;
};
/**
 * The built-in scale written out as points, for an office that wants to start
 * from it and then change something.
 *
 * Not an approximation any more. The scale is a straight line between 500 and
 * 3000 and flat outside them, and so is a table of points taken from it, so
 * these rows reproduce the scale exactly at every rent rather than only at the
 * rows themselves. That is what interpolation bought us (DEV-3265).
 *
 * Six rows rather than the two the line strictly needs, because the screen
 * shows the same six when describing the standard, and a broker who presses
 * personalize should see what they just read.
 */
export var getStandardIncomePoints = function (step, from, to) {
    if (step === void 0) { step = 500; }
    if (from === void 0) { from = 500; }
    if (to === void 0) { to = 3000; }
    var points = [];
    for (var rent = from; rent <= to; rent += step) {
        points.push({
            maxRent: rent,
            ratio: Math.round(getStandardPerfectRatio(rent) * 100) / 100,
        });
    }
    return points;
};
/**
 * Which end of the curve a row closes off, if it closes one off at all.
 *
 * Below the first point and above the last the multiple stays flat, so those
 * two rows speak for every rent beyond them, and the screens say so in words:
 * "500 and below", "3,000 and above" (Jonas, 2026-09-24). The rows in between
 * are points and need no label.
 *
 * A table of one point is flat everywhere. Labeling that single row "and below"
 * would be half the truth, so it gets no label at all.
 */
export var getIncomePointEdge = function (index, count) {
    if (count < 2) {
        return undefined;
    }
    if (index === 0) {
        return "below";
    }
    if (index === count - 1) {
        return "above";
    }
    return undefined;
};
export var INCOME_POINT_EDGE_KEY = {
    below: "broker.rentmatch.income_ranges.row.and_below",
    above: "broker.rentmatch.income_ranges.row.and_above",
};
/** The sliding scale, which is what a property without settings is scored by. */
var getDefaultRatios = function (rent) {
    var rentFactor = Math.max(0, Math.min(1, (rent - 500) / 2500));
    return {
        perfectRatio: 3.0 - 1.0 * rentFactor, // 3.0× at €500 → 2.0× at €3000+
        minRatio: 2.0 - 0.5 * rentFactor, // 2.0× at €500 → 1.5× at €3000+
    };
};
/**
 * The income below which the income score is zero, as a multiple of the rent,
 * from the multiple a perfect score asks for.
 *
 * ONE named function on purpose. It is the only place the lower threshold is
 * decided, so a later change to that rule is one edit per copy. Nothing else
 * depends on how it is worked out: the floor is derived and never shown, so no
 * screen and no stored setting knows about it.
 *
 * It moves in proportion rather than by a constant number of euros, so a broker
 * who is lenient at the top is lenient at the bottom in the same measure.
 * Keeping the gap constant would still award points, at a lenient setting, to
 * somebody earning barely more than the rent.
 *
 * It does not clamp. The lowest amount a broker may enter is enforced where the
 * broker enters it and where it is stored; correcting a bad stored value here
 * would hide it instead, and a hidden correction to a number nobody can see is
 * how a rule comes to mean something other than what it says.
 */
var deriveMinRatio = function (perfectRatio, fallback) {
    return fallback.perfectRatio > 0
        ? fallback.minRatio * (perfectRatio / fallback.perfectRatio)
        : fallback.minRatio;
};
/**
 * What the office's curve asks at one rent.
 *
 * Straight between two points, flat outside the outermost ones. A single point
 * is therefore flat everywhere, which is a perfectly good table: one multiple
 * for every rent.
 *
 * Flat at the ends rather than extrapolated: continuing the last slope would
 * eventually cross zero and start rewarding a candidate for the rent being
 * high, and an office that stops describing rents above 3000 has said nothing
 * about them beyond "the same as at 3000".
 */
var getRatioFromRanges = function (ranges, rent) {
    if (!ranges || ranges.length === 0) {
        return undefined;
    }
    var first = ranges[0];
    var last = ranges[ranges.length - 1];
    if (rent <= first.maxRent) {
        return first.ratio;
    }
    if (rent >= last.maxRent) {
        return last.ratio;
    }
    var upperIndex = ranges.findIndex(function (point) { return rent <= point.maxRent; });
    var upper = ranges[upperIndex];
    var lower = ranges[upperIndex - 1];
    var span = upper.maxRent - lower.maxRent;
    return (lower.ratio + ((upper.ratio - lower.ratio) * (rent - lower.maxRent)) / span);
};
/**
 * What this property asks of a candidate's income, as multiples of the rent:
 * the one that scores full marks and the one below which it scores nothing.
 */
function getIncomeThresholds(rent, adults, minors, settings) {
    var _a, _b;
    var fallback = getDefaultRatios(rent);
    // Three sources, narrowest last: the sliding scale, the office price table,
    // and what the broker said about this one property. The broker names an
    // amount there rather than a multiple, because an amount is what they think
    // in; it becomes one here.
    var fromTable = getRatioFromRanges(settings === null || settings === void 0 ? void 0 : settings.incomeRatioRanges, rent);
    var fromProperty = (settings === null || settings === void 0 ? void 0 : settings.perfectIncome) && rent > 0
        ? settings.perfectIncome / rent
        : undefined;
    var perfectRatio = (_a = fromProperty !== null && fromProperty !== void 0 ? fromProperty : fromTable) !== null && _a !== void 0 ? _a : fallback.perfectRatio;
    // Never shown anywhere. Showing a number a broker cannot set only invites the
    // question why not.
    var minRatio = deriveMinRatio(perfectRatio, fallback);
    // Above the household the broker allowed for, both rise by a tenth per extra
    // person, on top of whatever the broker set.
    //
    // Counted from the broker's own maximum, not always from two. The screen
    // promises a perfect score from 1800 and with at most three people, so on a
    // property with a maximum of three, 1800 has to be exactly the bar for
    // three: counting
    // the surcharge from two asked 1890 of them and made that sentence false. An
    // unset maximum still counts from two, so nothing moves on a property nobody
    // configured, which is almost all of them.
    var surchargeFrom = (_b = settings === null || settings === void 0 ? void 0 : settings.maxHousehold) !== null && _b !== void 0 ? _b : 2;
    var totalPeople = adults + minors;
    if (totalPeople > surchargeFrom) {
        var surcharge = (totalPeople - surchargeFrom) * 0.1;
        minRatio += surcharge;
        perfectRatio += surcharge;
    }
    return { perfectRatio: perfectRatio, minRatio: minRatio };
}
/**
 * The income this property asks for a perfect score, in euros, for an ordinary
 * household of one or two.
 *
 * What the settings screen shows a broker as the starting point: the office
 * table if they filled one in, otherwise the built-in scale. The household
 * surcharge is deliberately left out, because the broker is setting the bar for
 * an ordinary household and the surcharge is added on top of whatever they set.
 *
 * Rounded to whole euros. The built-in scale slides with the rent and lands on
 * amounts like 1948.4416, and the number is read off a screen rather than
 * charged to anybody.
 */
export var getPerfectIncomeForRent = function (rent, settings) { return Math.round(rent * getIncomeThresholds(rent, 1, 0, settings).perfectRatio); };
function calcIncomePct(user, property) {
    var totalIncome = user.netIncome + user.otherIncome;
    if (totalIncome <= 0)
        return 0;
    var ratio = totalIncome / property.rent;
    var _a = getIncomeThresholds(property.rent, user.adults, user.minors, property.settings), perfectRatio = _a.perfectRatio, minRatio = _a.minRatio;
    if (ratio >= perfectRatio)
        return 1.0;
    if (ratio <= minRatio)
        return 0;
    return (ratio - minRatio) / (perfectRatio - minRatio);
}
// ── B. Household helpers ──────────────────────────────────────────────
/** How many rooms this household needs, which is what "spare" is measured
 * against. */
var getRoomsNeeded = function (user) {
    // The first room holds two people, and the rule used to assume those two were
    // always adults. For a lone parent the second place in that room then stayed
    // empty while the child was charged 0.75 of a room anyway, so one adult with
    // one child scored 25 where a couple scored 100 on the same one-bedroom flat,
    // for a household of exactly the same size.
    //
    // One adult, so one free place beside them: the first child takes it. From the
    // second child on, and for every household with two adults or more, nothing
    // changes.
    var childrenSharingTheFirstRoom = user.adults === 1 ? 1 : 0;
    // First 2 adults share 1 room (couple), extra adults each need a room
    // Children: 0.75 rooms per child (≈ 2 children per 1.5 rooms)
    return (1 +
        Math.max(0, user.adults - 2) +
        Math.max(0, user.minors - childrenSharingTheFirstRoom) * 0.75);
};
/**
 * A property with more rooms than the household needs does not reach a full
 * household score while the income is short of perfect. Applied to whatever the
 * household rule worked out, including what the broker's own number worked out,
 * because being oversized is a fact about the rooms and not about how many
 * people may live there.
 */
var applyOversizedCap = function (pct, user, property, incomePct) {
    if (property.bedrooms === null || property.bedrooms === undefined) {
        return pct;
    }
    var isOversized = property.bedrooms > Math.ceil(getRoomsNeeded(user));
    return isOversized && incomePct < 1.0 ? Math.min(pct, 0.9) : pct;
};
function calcHouseholdPct(user, property, incomePct) {
    var _a;
    var occupants = user.adults + user.minors;
    // ABOVE EVERYTHING ELSE, INCLUDING THE STUDENT RESIDENCE, and that inverts
    // what used to be guaranteed here. The rule below used to sit first
    // precisely so that no override could reach a kot; the broker now decides
    // for every property, whatever its type: the type is not to be taken into
    // account here, the broker always chooses and can always change it. A test
    // asserts the inversion, so nobody restores the old order thinking they are
    // fixing something.
    //
    // Nothing is set on almost every property, and then none of this runs and the
    // room rule below decides exactly as it did before.
    var maxHousehold = (_a = property.settings) === null || _a === void 0 ? void 0 : _a.maxHousehold;
    if (maxHousehold != null) {
        // Above the number, a person too many costs 0.75, which is what a child
        // costs in the room rule. Above the number we no longer know who the extra
        // person is, so it takes the more common case. It may go negative, like the
        // room rule it replaces.
        var tooMany = Math.max(0, occupants - maxHousehold);
        var pct_1 = 1 - tooMany * 0.75;
        // The number says how many people may live here. It says nothing about a
        // property being larger than the household needs, so the cap on an
        // oversized property is still worked out from the rooms: the number
        // replaces the reckoning of what is missing, never of what is spare.
        // Without this, setting any number would quietly switch off a behavior
        // that is meant: four adults in a four-bedroom on an imperfect income score
        // 90, and they keep scoring 90 whatever the broker typed.
        return applyOversizedCap(pct_1, user, property, incomePct);
    }
    // A student residence is a single room let to a single student, and unlike
    // every other type that is not a matter of size but of what the property is
    // allowed to be. So the leniency towards an empty bedroom count below does
    // not apply here: unknown and zero both mean one room, and anything above one
    // person scores nothing at all.
    //
    // Zero is enough on its own, because a full household score is 40 of 100 and
    // the no-match band starts below 65, so no income and no move-in date can
    // lift a second occupant back over the line.
    //
    // A kot that does state a real bedroom count is not the case this exists for,
    // and falls through to the general rule.
    if (property.typeId === EPropertyTypeId.StudentResidence &&
        (property.bedrooms === null || property.bedrooms === 0)) {
        return occupants <= 1 ? 1.0 : 0;
    }
    // Unknown is not the same as none. An empty bedroom count is a gap in the
    // listing, and a candidate is not punished for it, so it scores full marks.
    if (property.bedrooms === null)
        return 1.0;
    // Zero IS a real answer, and it means a studio. The Whise import has stored a
    // genuine 0 as 0 for a while now, and the general rule below reads that as
    // "not even one bedroom": a single occupant came up a whole room short, so a
    // studio scored a no match for the one household it actually suits. One
    // person fits a studio perfectly.
    //
    // Above one person the general rule already puts every household under the
    // no-match threshold on its own, because a full household score is 40 of 100
    // and the threshold is 65. It is left to do that rather than flattened to a
    // single "no", so the bars and the improvement tips keep saying how far off a
    // household is: a couple lands on 60 and a family of four on 8.
    if (property.bedrooms === 0 && occupants <= 1)
        return 1.0;
    var roomsNeeded = getRoomsNeeded(user);
    var excess = Math.max(0, roomsNeeded - property.bedrooms);
    // Can go negative — penalties eat into other categories' scores
    var pct = 1 - excess;
    return applyOversizedCap(pct, user, property, incomePct);
}
/**
 * The largest household this property gives a full household score today, which
 * is what the settings screen prefills the broker's number with.
 *
 * Worked out by asking the scoring itself rather than by a formula or a table
 * kept beside it. The prefill exists so that a broker who opens the form and
 * saves it without thinking changes no score at all, and only the scoring knows
 * what it would otherwise have said. A formula would have to be kept in step by
 * hand, in three repos, and "bedrooms plus one" is already wrong at four: two
 * adults share a room and four children cost 0.75 each, which is exactly four
 * rooms, so a four-bedroom allows six.
 *
 * Every composition of a household of that size is tried, because rooms are not
 * people: six adults need five rooms where two adults with four children need
 * four.
 *
 * Undefined when the bedroom count is unknown, where every household already
 * scores full marks and there is no largest one to name.
 */
export var getMaxFullScoreHousehold = function (bedrooms) {
    if (bedrooms === null || bedrooms === undefined) {
        return undefined;
    }
    var property = {
        rent: 1000,
        bedrooms: bedrooms,
        availableDate: null,
        hasBalcony: false,
        hasGarden: false,
        floorType: "other",
    };
    var scoresFull = function (people) {
        for (var adults = 1; adults <= people; adults++) {
            var user = {
                netIncome: 0,
                otherIncome: 0,
                adults: adults,
                minors: people - adults,
                moveInDateType: "flexible",
            };
            // A perfect income, so the cap on an oversized property never colors the
            // answer: this asks how many people fit, not how well they pay.
            if (calcHouseholdPct(user, property, 1) >= 1) {
                return true;
            }
        }
        return false;
    };
    var largest = 0;
    // Far past anything a property offers, and it stops at the first size that
    // does not fit, so the bound only guards against a rule change making every
    // size fit.
    for (var people = 1; people <= 20; people++) {
        if (!scoresFull(people)) {
            break;
        }
        largest = people;
    }
    return largest || undefined;
};
// ── C. Date helpers ───────────────────────────────────────────────────
//
// The date comparison below exists three times and the three must agree,
// otherwise the same candidate gets one score in the mail, another one on the
// calculator and a third one in this dashboard:
//   1. rentmatch/src/rentmatch/utils/scoreCalculator.ts        (calculator + candidate match list)
//   2. rentio-api-serverless/src/domains/tenantProfile/worker/
//        rentMatchNotification/helper/scoreCalculator.ts       (digest mail)
//   3. this file                                               (broker dashboard)
// They are kept separate on purpose (three repos, three release trains, and
// this copy carries dashboard-only extras), so changing the rules here means
// changing them in the other two as well. The two copies that have a test
// runner share a table of worked-out cases named SHARED_DATE_CASES; keep that
// table in step with any change made here.
var DAY_IN_MS = 86400000;
// Whole calendar days between two dates, counted on the UTC calendar.
//
// Subtracting two local midnights in milliseconds is off by an hour whenever a
// daylight-saving switch falls inside the interval, which was enough to push an
// exactly-30-day gap just outside the 30 day tolerance twice a year. Mapping
// each date to a day number first keeps the comparison on whole days.
var toDayIndex = function (date) {
    return Math.floor(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / DAY_IN_MS);
};
// Both dates are pulled forward to today before they are compared.
//
// A property standing empty is available *from today*, not from the date in
// the past when it first became free. Comparing against that past date
// punished exactly the properties that suit someone who wants to move
// immediately: a flat empty for a year scored 1 out of 100 for a candidate who
// could move in tomorrow. It also measured how long the property had already
// been vacant, which the candidate cannot influence.
//
// The same holds for the candidate's own date: a desired move-in date in the
// past means "as soon as possible". Left as it was, it was the shortest route
// to a perfect score.
//
// Wanting to move later than the property is free stays a penalty on purpose:
// a candidate who can move sooner will be preferred, so the lower score is
// honest. The penalty now measures how many extra empty weeks the landlord
// faces because of this candidate.
//
// "ASAP" tolerates a month, a named date tolerates three days. Both branches
// reach this helper for the ASAP case so those two tolerances cannot drift
// apart again: a candidate whose named date has already passed means ASAP, and
// scoring them on the three-day tolerance cost them 15 points against an
// identical candidate whose intent was read as ASAP.
var scoreAsSoonAsPossible = function (availableFrom, todayIndex) {
    var daysApart = availableFrom - todayIndex;
    if (daysApart <= 30)
        return 1.0;
    // Not clamped at 0 on purpose: a date far out of reach has to be able to
    // disqualify, otherwise it could never cost more than its own 10 points.
    return 1 - (daysApart - 30) / 20;
};
function calcDatePct(user, property) {
    if (!property.availableDate)
        return 1.0;
    if (user.moveInDateType === "flexible")
        return 1.0;
    if (user.moveInDateType === "" || user.moveInDateType === undefined)
        return 0; // not filled → 0 pts
    var availDate = new Date(property.availableDate);
    // A value that cannot be read as a date (e.g. the MySQL zero date
    // "0000-00-00") is not a real constraint, so treat it like a missing date
    // instead of producing NaN scores. Same guard as rentmatch's own copy.
    if (Number.isNaN(availDate.getTime()))
        return 1.0;
    var todayIndex = toDayIndex(new Date());
    var availableFrom = Math.max(toDayIndex(availDate), todayIndex);
    if (user.moveInDateType === "immediately") {
        return scoreAsSoonAsPossible(availableFrom, todayIndex);
    }
    if (user.moveInDateType === "specific" && user.specificMoveInDate) {
        var specificDate = new Date(user.specificMoveInDate);
        if (Number.isNaN(specificDate.getTime()))
            return 0;
        var wantedIndex = toDayIndex(specificDate);
        // A named date that has already passed says the same thing as ASAP, so it
        // is scored the same way rather than on the tighter tolerance a
        // deliberately chosen future date gets. A candidate whose occupancy date
        // sits in the past means "as soon as you can have me", not "on that exact
        // day".
        if (wantedIndex <= todayIndex) {
            return scoreAsSoonAsPossible(availableFrom, todayIndex);
        }
        var daysApart = Math.abs(availableFrom - wantedIndex);
        if (daysApart <= 3) {
            return 1.0;
        }
        // Same penalty for too early AND too late, measured from today.
        return 1 - daysApart / 20;
    }
    return 0;
}
// ── Warnings ──────────────────────────────────────────────────────────
function calcWarnings(user, property, incomePct, householdPct, datePct) {
    var warnings = [];
    var totalIncome = user.netIncome + user.otherIncome;
    // Income warnings
    if (totalIncome > 0) {
        if (incomePct >= 0.8 && incomePct < 1.0) {
            warnings.push({
                type: "orange",
                message: getLocalizedText("rentmatch.score_warning.income_slightly_below"),
            });
        }
        else if (incomePct >= 0.5 && incomePct < 0.8) {
            warnings.push({
                type: "orange",
                message: getLocalizedText("rentmatch.score_warning.income_below"),
            });
        }
        else if (incomePct > 0 && incomePct < 0.5) {
            warnings.push({
                type: "orange",
                message: getLocalizedText("rentmatch.score_warning.income_significantly_below"),
            });
        }
        else if (incomePct === 0 && totalIncome > 0) {
            warnings.push({
                type: "red",
                message: getLocalizedText("rentmatch.score_warning.income_too_low"),
            });
        }
    }
    // Household warnings
    var roomsNeeded = 1 + Math.max(0, user.adults - 2) + user.minors * 0.75;
    if (property.bedrooms !== null) {
        if (property.bedrooms > Math.ceil(roomsNeeded) && incomePct < 1.0) {
            warnings.push({
                type: "orange",
                message: getLocalizedText("rentmatch.score_warning.fewer_bedrooms_recommended"),
            });
        }
        else if (householdPct < 1.0 && householdPct > 0) {
            warnings.push({
                type: "orange",
                message: getLocalizedText("rentmatch.score_warning.household_high", {
                    bedrooms: String(property.bedrooms),
                }),
            });
        }
        else if (householdPct <= 0) {
            warnings.push({
                type: "red",
                message: getLocalizedText("rentmatch.score_warning.household_too_large", {
                    bedrooms: String(property.bedrooms),
                }),
            });
        }
    }
    // Date warnings
    if (property.availableDate) {
        if (user.moveInDateType === "immediately" && datePct < 1.0 && datePct > 0) {
            warnings.push({
                type: "orange",
                message: getLocalizedText("rentmatch.score_warning.move_in_not_available"),
            });
        }
        else if (datePct === 0 &&
            user.moveInDateType !== "" &&
            user.moveInDateType !== "flexible") {
            warnings.push({
                type: "red",
                message: getLocalizedText("rentmatch.score_warning.move_in_too_early"),
            });
        }
    }
    return warnings;
}
// ── Main scoring function ─────────────────────────────────────────────
export function calculateRentMatchScore(user, property) {
    var incomePct = calcIncomePct(user, property);
    var householdPct = calcHouseholdPct(user, property, incomePct);
    var datePct = calcDatePct(user, property);
    var warnings = calcWarnings(user, property, incomePct, householdPct, datePct);
    // Additive: each category contributes weight × pct
    // Household and date CAN go negative, subtracting from income's score
    // Income stays 0–1 (no negative income)
    var rawScore = incomePct * WEIGHTS.income +
        householdPct * WEIGHTS.household +
        datePct * WEIGHTS.date;
    // Income gate: below minRatio, further scale down
    var totalIncome = user.netIncome + user.otherIncome;
    var finalScore = rawScore;
    if (totalIncome > 0) {
        var ratio = totalIncome / property.rent;
        // With the property's settings, like every other read of this threshold.
        // Without them the gate fell back to the built-in scale and quietly
        // contradicted the broker: on a property set to a perfect income of 1800
        // with at most three people, a household of three earning exactly 1800
        // scored 99 instead of 100, because the fallback floor still counted a
        // third person as a surcharge and landed just above their ratio.
        var minRatio = getIncomeThresholds(property.rent, user.adults, user.minors, property.settings).minRatio;
        if (ratio < minRatio) {
            // Only scale positive scores. Multiplying a negative score by a fraction < 1
            // artificially shrinks it towards 0, making a terrible match look 'better'.
            if (rawScore > 0) {
                finalScore = rawScore * (ratio / minRatio);
            }
        }
    }
    // Asymptotic curve to ensure the score keeps dropping but never reaches 0 (or goes negative)
    var displayScore = finalScore;
    if (finalScore < 15) {
        displayScore = 15 * Math.exp((finalScore - 15) / 25);
    }
    return {
        score: Math.max(1, Math.min(100, Math.round(displayScore))),
        warnings: warnings,
        incomePct: incomePct,
        householdPct: householdPct,
        datePct: datePct,
    };
}
